import React, { useEffect, useState } from "react";
import { Clock3, Loader2, Save, ShieldCheck } from "lucide-react";
import api from "@/api";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const OPTIONS = [5, 10, 15, 20, 30, 45, 60, 90, 120];

export default function SessionPreferencesSettings() {
  const [settings, setSettings] = useState(null);
  const [minutes, setMinutes] = useState(10);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    let active = true;
    api.getSettings()
      .then((data) => {
        if (!active) return;
        const configured = Number(data?.inactivityTimeoutMinutes);
        setSettings(data);
        setMinutes(
          Number.isFinite(configured) && configured >= 5 && configured <= 120
            ? configured
            : 10
        );
      })
      .catch(() => {
        if (active) setMessage("Impossibile caricare le preferenze.");
      });
    return () => { active = false; };
  }, []);

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    setMessage("");
    try {
      const nextSettings = { ...settings, inactivityTimeoutMinutes: minutes };
      await api.updateSettings(nextSettings);
      setSettings(nextSettings);
      window.dispatchEvent(
        new CustomEvent("gi:inactivity-timeout-changed", {
          detail: { minutes },
        })
      );
      setMessage("Preferenza salvata e applicata.");
    } catch (error) {
      setMessage(error?.message || "Errore durante il salvataggio.");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <div className="flex items-center gap-3 py-12 justify-center text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" />
        Caricamento preferenze...
      </div>
    );
  }

  const savedMinutes = Number(settings.inactivityTimeoutMinutes || 10);
  const isSuccess = message.startsWith("Preferenza");

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl shadow-sm p-5 sm:p-6"
      data-testid="session-preferences-settings"
    >
      <div className="flex items-start gap-3 pb-5 border-b border-slate-100">
        <span className="h-10 w-10 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0">
          <Clock3 className="h-5 w-5 text-[var(--gis-green)]" />
        </span>
        <div>
          <h2 className="gis-heading text-xl font-bold text-slate-900">
            Tempo di inattività
          </h2>
          <p className="gis-body text-sm text-slate-500 mt-1">
            Dopo questo periodo il gestionale richiede nuovamente la password.
          </p>
        </div>
      </div>

      <div className="mt-6 max-w-2xl">
        <label className="gis-heading text-xs font-semibold uppercase tracking-wide text-slate-600">
          Uscita automatica dopo
        </label>
        <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-3">
          <Select
            value={String(minutes)}
            onValueChange={(value) => setMinutes(Number(value))}
          >
            <SelectTrigger className="w-full sm:w-56" data-testid="inactivity-timeout-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {OPTIONS.map((value) => (
                <SelectItem key={value} value={String(value)}>
                  {value} minuti
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <span className="inline-flex items-center gap-2 text-sm text-slate-500">
            <ShieldCheck className="h-4 w-4 text-[var(--gis-green)]" />
            Attualmente: {savedMinutes} minuti
          </span>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-slate-500">
          Contano click, tastiera, scorrimento e tocco. Il semplice movimento
          del mouse non prolunga la sessione.
        </p>
      </div>

      <div className="mt-6 pt-5 border-t border-slate-100 flex items-center justify-between gap-4">
        <p
          className={`text-sm ${isSuccess ? "text-emerald-700" : "text-red-600"}`}
          role="status"
        >
          {message}
        </p>
        <Button
          onClick={save}
          disabled={saving || minutes === savedMinutes}
          className="bg-[var(--gis-green)] hover:bg-[var(--gis-green-hover)]"
          data-testid="save-inactivity-timeout"
        >
          {saving
            ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            : <Save className="h-4 w-4 mr-2" />}
          Salva preferenza
        </Button>
      </div>
    </section>
  );
}
